import type { SolChainConfig } from "@shared/types";

/** The wallet-standard cluster ids (the `@solana/wallet-standard-chains` values). */
export type SolWalletStandardChain = "solana:mainnet" | "solana:devnet" | "solana:testnet";

const NETWORK_TO_CHAIN: Record<string, SolWalletStandardChain> = {
    solana: "solana:mainnet",
    soltest: "solana:testnet",
    soldev: "solana:devnet",
};

export const toWalletStandardChain = (chainConfig: SolChainConfig): SolWalletStandardChain => {
    if (!chainConfig) throw new Error("A Solana chain config is required to send a transaction");

    return (
        NETWORK_TO_CHAIN[chainConfig.network.toLowerCase()] ??
        (chainConfig.is_testnet ? "solana:devnet" : "solana:mainnet")
    );
};

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/**
 * Decode base64 to raw bytes. `atob` is what `paymentsWidget/utils.ts` already uses — the host runs
 * in a browser — and it is byte-exact, unlike a `TextEncoder` round-trip.
 *
 * @param {string} base64 The base64-encoded input.
 * @returns {Uint8Array} The decoded bytes.
 */
export const fromBase64 = (base64: string): Uint8Array => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
};

/**
 * Encode big-endian bytes as base58. Local rather than from HallidayLib's `b58.ts`: that module is
 * not in `payments/tsconfig.json`'s `include` list and exposes no byte-array encoder.
 *
 * @param {Uint8Array} bytes The bytes to encode, most-significant first.
 * @returns {string} The base58 encoding, using the Bitcoin/Solana alphabet.
 */
export const toBase58 = (bytes: Uint8Array): string => {
    let leadingZeros = 0;
    while (leadingZeros < bytes.length && bytes[leadingZeros] === 0) leadingZeros += 1;

    // Base-256 -> base-58 by repeated multiply-and-add over little-endian digits.
    const digits: number[] = [];
    for (let i = leadingZeros; i < bytes.length; i += 1) {
        let carry = bytes[i] ?? 0;
        for (let j = 0; j < digits.length; j += 1) {
            carry += (digits[j] ?? 0) << 8;
            digits[j] = carry % 58;
            carry = (carry / 58) | 0;
        }
        while (carry > 0) {
            digits.push(carry % 58);
            carry = (carry / 58) | 0;
        }
    }

    // Each leading 0x00 byte is one leading '1'; the digits are emitted big-endian.
    let encoded = "1".repeat(leadingZeros);
    for (let k = digits.length - 1; k >= 0; k -= 1) encoded += BASE58_ALPHABET.charAt(digits[k] ?? 0);
    return encoded;
};
